import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import type { Locale } from '@/types/regionTypes';
import { getAspectRatioForBoutiqueImages } from '@/utils/aspectRatioUtils';

import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import type { StoreHit } from '@/integrations/algolia/types';
import { getCountryNameFromCode } from '@/lib/boutique/helpers';
import { BASE_STORE_FINDER_URL } from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import { generateDataTestId } from '@/lib/tests';
import { messages as boutiqueMessages } from '@/messages/boutique';
import { myAccountMessages } from '@/messages/myAccount';
import * as M from '@/messages/wishlist';
import { useRegionProvider } from '@/providers/RegionProvider';

import { AspectRatioImage } from '../AspectRatioImage/AspectRatioImage';
import { Icon } from '../Icon';
import type { NextImageProps } from '../NextImage';
import { getTranslatedStoreValue } from '../StoresList/utils';

import * as S from './BoutiqueCard.styles';

export interface BoutiqueDetail {
  Boutique: StoreHit;
  className?: string;
  index?: number;
  isMobileView: boolean;
  placeholderStoresColor?: string;
  type?: string;
}
export interface BoutiqueDetailHasCTA extends BoutiqueDetail {
  clickEmail: () => string;
  mapUrl?: string;
  navtoDetail: () => string;
  onEvent: () => void;
}

export const BoutiqueCard = ({
  Boutique,
  className,
  index,
  isMobileView,
  placeholderStoresColor,
  type,
}: BoutiqueDetail) => {
  const { country, locale } = useRegionProvider();
  const { paths } = useRouter();
  const { slug } = Boutique;
  const { sendEvent } = useGoogleAnalytics();
  const LANG_MAP = {
    'en-US': 'en',
    'en-GB': 'en',
  };

  const mapUrl = paths.stores.breitlingBoutiques.asPath({
    country: country.slug,
    slug: slug,
    query: { type: 'googleMap' },
  });
  const handleEvent = () => {
    sendEvent({
      event: EVENT_NAME.CTA_DISCOVER,
      cta_title: Boutique.name,
      click_cta_name: 'Book An Appointment',
    });
  };
  const getStoreDetailUrl = (appointment = true) => {
    const path = appointment
      ? paths.stores.appointment
      : paths.stores.breitlingBoutiques;
    if (
      Boutique.type === 'FLAG_SHIP_BOUTIQUE' ||
      Boutique.type === 'BOUTIQUE'
    ) {
      return path.asPath({
        country: country.slug,
        slug: slug,
      });
    } else {
      const lang: string =
        LANG_MAP[locale.locale as keyof typeof LANG_MAP] ??
        locale.locale ??
        'en';
      return `${BASE_STORE_FINDER_URL}${lang.toLowerCase()}?storeId=${
        Boutique.store_id
      }`;
    }
  };

  switch (type) {
    case 'Basic':
      return (
        <BasicBoutiqueCard
          Boutique={Boutique}
          clickEmail={() => getStoreDetailUrl(false)}
          data-test-id='BoutiqueCardBasic'
          index={index}
          isMobileView={isMobileView}
          mapUrl={mapUrl}
          navtoDetail={getStoreDetailUrl}
          onEvent={handleEvent}
        ></BasicBoutiqueCard>
      );

    case 'Manual':
    case 'EyewearAvailable':
    case 'Default':
      return (
        <IntricateBoutiqueCard
          Boutique={Boutique}
          className={className}
          clickEmail={() => getStoreDetailUrl(false)}
          data-test-id='BoutiqueCardDefault'
          index={index}
          isMobileView={isMobileView}
          mapUrl={mapUrl}
          navtoDetail={getStoreDetailUrl}
          onEvent={handleEvent}
          placeholderStoresColor={placeholderStoresColor}
        ></IntricateBoutiqueCard>
      );
    case 'Search':
      return (
        <SearchTypeBoutiqueCard
          Boutique={Boutique}
          className={className}
          clickEmail={() => getStoreDetailUrl(false)}
          data-test-id='BoutiqueCardDefault'
          index={index}
          isMobileView={isMobileView}
          navtoDetail={() => {
            if (
              Boutique.type === 'FLAG_SHIP_BOUTIQUE' ||
              Boutique.type === 'BOUTIQUE'
            ) {
              return paths.stores.breitlingBoutiques.asPath({
                country: Boutique.country_code?.toLowerCase(),
                slug: slug,
              });
            }
            const lang: string =
              LANG_MAP[locale.locale as keyof typeof LANG_MAP] ??
              locale.locale ??
              'en';
            return `${BASE_STORE_FINDER_URL}${lang.toLowerCase()}?storeId=${
              Boutique.store_id
            }`;
          }}
          onEvent={handleEvent}
          placeholderStoresColor={placeholderStoresColor}
        ></SearchTypeBoutiqueCard>
      );
    default:
      return <></>;
  }
};

export interface ImageProps extends Pick<NextImageProps, 'src' | 'alt'> {
  aspectRatio?: { x: number; y: number };
  className?: string;
  focalPoint?: { x: number; y: number };
  imageDimensions?: { x: number; y: number };
  noStoreResults?: boolean;
  priority?: boolean;
  scaleRatio?: number;
  withBackground?: boolean;
  withBackgroundPlaceholderBackground?: boolean;
}

export const BoutiqueImage = ({
  alt,
  aspectRatio,
  className,
  focalPoint,
  imageDimensions,
  noStoreResults,
  priority,
  scaleRatio,
  src,
  withBackground = true,
  withBackgroundPlaceholderBackground,
}: ImageProps) => {
  const showAspectRatioImage = aspectRatio && focalPoint && imageDimensions;
  return (
    <>
      {showAspectRatioImage && (
        <AspectRatioImage
          alt={alt}
          aspectRatio={aspectRatio}
          className='boutiquecard-image'
          data-test-id='BoutiqueImageAspectRatioImage'
          focalPoint={focalPoint}
          loaderProps={{ height: 245 }}
          loading='lazy'
          originalImageDimensions={imageDimensions}
          showFallbackPlaceholder
          showPlaceholderBackground={withBackgroundPlaceholderBackground}
          sizes='(max-width: 600px) 50vw, 20vw'
          src={src as string}
          useLoader
          width='100%'
          {...generateDataTestId('productCard', 'image')}
        />
      )}
      {!showAspectRatioImage && (
        <S.Img
          $noStoreResults={noStoreResults}
          $scaleRatio={scaleRatio}
          $withBackground={withBackground}
          alt={alt}
          aria-hidden
          className={className}
          data-test-id='BoutiqueImageImg'
          height={245}
          loaderHeight={245}
          loading='lazy'
          priority={priority}
          showFallbackPlaceholder
          showPlaceholderBackground={withBackgroundPlaceholderBackground}
          sizes='(max-width: 640px) 90vw, (max-width: 768px) 70vw, 25vw'
          src={src ?? ''}
          width={328}
          {...generateDataTestId('productCard', 'image')}
        />
      )}
    </>
  );
};

type StoreHitLocalePropertyValue = string;
type StoreHitLocalePropertyName = 'name' | 'city' | 'street' | 'street2';

/*
 * locale.code is aligned with algolia's code
 */
export const getLocaleValue = (
  locale: Locale,
  propertyName: StoreHitLocalePropertyName,
  boutique: StoreHit,
): StoreHitLocalePropertyValue => {
  let _key: string;
  switch (locale.code) {
    case 'ZH_HANS':
      _key = `${propertyName}_zh_Hans`;
      break;
    case 'ZH_HANT':
      _key = `${propertyName}_zh_Hant`;
      break;
    case 'DE':
      _key = `${propertyName}_de_AT`;
      break;
    case 'EN_GB':
      _key = `${propertyName}_en`;
      break;
    default:
      _key = `${propertyName}_${locale.code.toLowerCase()}`;
      break;
  }
  return (
    boutique[_key as StoreHitLocalePropertyName] ||
    boutique[propertyName as StoreHitLocalePropertyName]
  );
};

export const BasicBoutiqueCard = ({
  Boutique,
  clickEmail,
  index,
  isMobileView,
  mapUrl,
  navtoDetail,
  onEvent,
}: BoutiqueDetailHasCTA) => {
  const { hours, phone } = Boutique;
  const { locale } = useRegionProvider();
  const name = getLocaleValue(locale, 'name', Boutique);
  const city = getLocaleValue(locale, 'city', Boutique);
  const street = getLocaleValue(locale, 'street', Boutique);
  const street2 = getLocaleValue(locale, 'street2', Boutique);
  const today = dayjs();
  const weekday = today.format('dddd').toLowerCase();
  return isMobileView ? (
    <S.BoutiqueMobile
      key={index}
      data-test-id={`BasicBoutiqueCard-${index}`}
      flex={1}
    >
      <S.BoutiqueTopInfo>
        <S.BoutiqueTitle data-test-id='BasicBoutiqueCardBoutiqueTitle'>
          {name}
        </S.BoutiqueTitle>
        <S.AdressBox>
          <Icon
            data-test-id='BasicBoutiqueCardChevronUp'
            name='chevronUp'
            size='s'
            variant='regular'
          />
          <S.Info data-test-id='BasicBoutiqueCardInfo'>
            {street}
            {street2 && <br />}
            {street2}
            <br />
            {Boutique.postal_code && `${Boutique.postal_code} `}
            {city}
            {Boutique.country_code &&
              `, ${getCountryNameFromCode(Boutique.country_code)}`}
          </S.Info>
        </S.AdressBox>

        <S.InfoBox>
          <Icon
            data-test-id='BasicBoutiqueCardTelephoneCall'
            name='telephoneCall'
            size='s'
            variant='regular'
          />
          <S.PhoneInfo data-test-id='BasicBoutiqueCardPhoneInfo'>
            {phone}
          </S.PhoneInfo>
        </S.InfoBox>

        {hours && (
          <S.InfoBox>
            <S.CalendarIcon />
            <S.Info data-test-id='BasicBoutiqueCardInfo'>
              {Object.entries(hours).map(([key, value]) => {
                if (key == weekday)
                  return (
                    <S.OpeningHourFlex key={key}>
                      <S.WeekDay>{key}:</S.WeekDay>
                      <S.WeekDayInfo>
                        {value.openIntervals
                          ? value.openIntervals[0].start +
                            '-' +
                            value.openIntervals[0].end
                          : 'Closed'}
                      </S.WeekDayInfo>
                    </S.OpeningHourFlex>
                  );
              })}
            </S.Info>
          </S.InfoBox>
        )}
      </S.BoutiqueTopInfo>
      <BoutiqueCTA
        Boutique={Boutique}
        clickEmail={clickEmail}
        isMobileView={isMobileView}
        mapUrl={mapUrl}
        navtoDetail={navtoDetail}
        onEvent={onEvent}
      />
    </S.BoutiqueMobile>
  ) : (
    <S.Boutique
      key={index}
      data-test-id={`BasicBoutiqueCard-${index}`}
      flex={1}
    >
      <S.BoutiqueTopInfo flexDirection='column'>
        <S.BoutiqueTitle data-test-id='BasicBoutiqueCardBoutiqueTitle'>
          {name}
        </S.BoutiqueTitle>
        <S.Info data-test-id='BasicBoutiqueCardInfo'>
          {street}
          {street2 && <br />}
          {street2}
          <br />
          {Boutique.postal_code && `${Boutique.postal_code} `}
          {city}
          {Boutique.country_code &&
            `, ${getCountryNameFromCode(Boutique.country_code)}`}
        </S.Info>
        <S.Info data-test-id='BasicBoutiqueCardInfo'>{phone}</S.Info>

        {hours && <S.OpeningHoursTitle>{M.OpeningHours}</S.OpeningHoursTitle>}
        {hours &&
          Object.entries(hours).map(([key, value]) => {
            return (
              <S.OpeningHourFlex key={key}>
                <S.WeekDay>{key}:</S.WeekDay>
                <S.WeekDayInfo>
                  {value.openIntervals
                    ? value.openIntervals[0].start +
                      '-' +
                      value.openIntervals[0].end
                    : 'Closed'}
                </S.WeekDayInfo>
              </S.OpeningHourFlex>
            );
          })}
      </S.BoutiqueTopInfo>
      <BoutiqueCTA
        Boutique={Boutique}
        clickEmail={clickEmail}
        isMobileView={isMobileView}
        mapUrl={mapUrl}
        navtoDetail={navtoDetail}
        onEvent={onEvent}
      />
    </S.Boutique>
  );
};
const BoutiqueCTA = ({
  clickEmail,
  mapUrl,
  navtoDetail,
  onEvent,
}: BoutiqueDetailHasCTA) => {
  const { formatMessage } = useIntl();
  return (
    <S.BoutiqueCTA>
      <S.BoutiqueBox>
        <S.BoutiqueButton
          data-testid='productCardMap'
          href={mapUrl}
          label={formatMessage(boutiqueMessages.placeholderStoresGetDirections)}
          onClick={onEvent}
          size='l'
          variant='link'
          width='auto'
        >
          <Icon name='chevronRight' size='s' />
        </S.BoutiqueButton>
        <S.BoutiqueButton
          data-testid='productCardViewMore'
          href={clickEmail()}
          label={formatMessage(boutiqueMessages.placeholderStoresViewMore)}
          onClick={onEvent}
          size='l'
          variant='link'
          width='auto'
        >
          <Icon name='chevronRight' size='s' />
        </S.BoutiqueButton>
      </S.BoutiqueBox>
      <S.AppointmentButton
        data-test-id='BasicBoutiqueCardDetail'
        href={navtoDetail()}
        onClick={onEvent}
        {...generateDataTestId('productCard', 'Detail')}
      >
        {formatMessage(boutiqueMessages.placeholderStoresBookAppointment)}
      </S.AppointmentButton>
    </S.BoutiqueCTA>
  );
};

export const IntricateBoutiqueCard = ({
  Boutique,
  className,
  clickEmail,
  index,
  mapUrl,
  navtoDetail,
  onEvent,
  placeholderStoresColor,
}: BoutiqueDetailHasCTA) => {
  const {
    focal_point: focalPoint,
    image_dimensions: imageDimensions,
    offers_appointment,
    phone,
    photo_gallery,
    thumbnail_url,
  } = Boutique;
  const { locale } = useRegionProvider();
  const name = getLocaleValue(locale, 'name', Boutique);
  const city = getLocaleValue(locale, 'city', Boutique);
  const street = getLocaleValue(locale, 'street', Boutique);
  const street2 = getLocaleValue(locale, 'street2', Boutique);
  const { deviceType } = useDeviceDetect();
  const { formatMessage } = useIntl();
  const storeImage = photo_gallery?.[0]?.image?.url ?? thumbnail_url;
  return (
    <S.IntricateBoutiqueCard
      key={index}
      $placeholderStoresColor={
        placeholderStoresColor ? placeholderStoresColor : ''
      }
      className={className}
      data-test-id={`IntricateBoutiqueCard-${index}`}
      flex={1}
    >
      <S.IntricateImage>
        <BoutiqueImage
          alt={name}
          aspectRatio={getAspectRatioForBoutiqueImages(deviceType)}
          data-testid='IntricateBoutiqueCardImage'
          focalPoint={focalPoint}
          imageDimensions={imageDimensions}
          src={storeImage ?? ''}
        ></BoutiqueImage>
      </S.IntricateImage>
      <S.IntricateInfoBox
        data-test-id='IntricateBoutiqueCardInfoBox'
        flexDirection='column'
      >
        <S.IntricateBoutiqueInfo>
          <S.IntricateTitle data-test-id='IntricateBoutiqueCardBoutiqueTitle'>
            {name}
          </S.IntricateTitle>
          <S.AdressBox>
            <S.LocationIcon></S.LocationIcon>
            <S.IntricateAddressInfo data-test-id='IntricateBoutiqueCardAddressInfo'>
              {street}
              {street2 && <br />}
              {street2}
              <br />
              {Boutique.postal_code && `${Boutique.postal_code} `}
              {city}
              {Boutique.country_code &&
                `, ${getCountryNameFromCode(Boutique.country_code)}`}
            </S.IntricateAddressInfo>
          </S.AdressBox>

          <S.InfoBox>
            <S.PhoneIcon />
            <S.IntricateAddressInfo data-test-id='IntricateBoutiqueCardPhoneInfo'>
              {phone}
            </S.IntricateAddressInfo>
          </S.InfoBox>
        </S.IntricateBoutiqueInfo>
        <S.IntricateCTA data-test-id='IntricateBoutiqueCardCTA'>
          <S.BoutiqueBox>
            <S.BoutiqueButton
              data-testid='IntricateBoutiqueCardMap'
              href={mapUrl}
              isUpperCase={false}
              label={formatMessage(
                boutiqueMessages.placeholderStoresGetDirections,
              )}
              onClick={onEvent}
              size='l'
              variant='link'
              width='auto'
            >
              <Icon name='chevronRight' size='s' />
            </S.BoutiqueButton>
            <S.BoutiqueButton
              data-testid='IntricateBoutiqueCardViewMore'
              href={clickEmail()}
              isUpperCase={false}
              label={formatMessage(boutiqueMessages.placeholderStoresViewMore)}
              onClick={onEvent}
              size='l'
              variant='link'
              width='auto'
            >
              <Icon name='chevronRight' size='s' />
            </S.BoutiqueButton>
          </S.BoutiqueBox>
          <S.IntricateButton
            data-testid='IntricateBoutiqueCardDetail'
            href={navtoDetail()}
            label={formatMessage(
              offers_appointment
                ? boutiqueMessages.placeholderStoresBookAppointment
                : boutiqueMessages.placeholderStoresViewDetails,
            )}
            onClick={onEvent}
            size='s'
            type='button'
            variant='outlined'
            width='100%'
          />
        </S.IntricateCTA>
      </S.IntricateInfoBox>
    </S.IntricateBoutiqueCard>
  );
};

export const SearchTypeBoutiqueCard = ({
  Boutique,
  className,
  index,
  navtoDetail,
  onEvent,
}: BoutiqueDetailHasCTA) => {
  const {
    focal_point: focalPoint,
    image_dimensions: imageDimensions,
    phone,
    photo_gallery,
    thumbnail_url,
  } = Boutique;

  const { locale } = useRegionProvider();
  const langSlug = locale.slug;
  const name = getTranslatedStoreValue(Boutique, 'name', langSlug);
  const addressText = () => {
    const street = getTranslatedStoreValue(Boutique, 'street', langSlug);
    const street2 = getTranslatedStoreValue(Boutique, 'street2', langSlug);
    const city = getTranslatedStoreValue(Boutique, 'city', langSlug);
    const postalCity = [Boutique.postal_code, city].filter(Boolean).join(' ');
    const country = getCountryNameFromCode(Boutique.country_code);
    const parts = [street, street2, postalCity, country].filter(Boolean);
    return parts.join(', ');
  };
  const { formatMessage } = useIntl();
  const { deviceType } = useDeviceDetect();
  const storeImage = photo_gallery?.[0]?.image?.url ?? thumbnail_url;
  return (
    <S.SearchTypeBoutiqueCard
      key={index}
      className={className}
      data-test-id={`SearchTypeBoutiqueCard-${index}`}
      href={navtoDetail()}
      onClick={onEvent}
    >
      <S.SearchTypeImageBox>
        <BoutiqueImage
          alt={name}
          aspectRatio={getAspectRatioForBoutiqueImages(deviceType)}
          data-testid='SearchTypeBoutiqueCardImage'
          focalPoint={focalPoint}
          imageDimensions={imageDimensions}
          src={storeImage ?? ''}
        ></BoutiqueImage>
      </S.SearchTypeImageBox>
      <S.SearchTypeBoutiqueInfo>
        <S.SearchTypeTitle data-testid='SearchTypeBoutiqueCardBoutiqueTitle'>
          {name}
        </S.SearchTypeTitle>
        <S.SearchTypeInfo data-testid='SearchTypeBoutiqueCardAddressInfo'>
          {addressText()}
        </S.SearchTypeInfo>
        <S.SearchTypeInfo data-testid='SearchTypeBoutiqueCardPhoneInfo'>
          {`${formatMessage(myAccountMessages.accountPhone)} ${phone}`}
        </S.SearchTypeInfo>
      </S.SearchTypeBoutiqueInfo>
    </S.SearchTypeBoutiqueCard>
  );
};
